import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

import { ROUTE_PATHS } from '../../../app/router/route-paths';
import { Button } from '../../../shared/ui/Button';
import type {
  AssistantCheck,
  AssistantParamOption,
  AssistantPlan,
  AssistantProposal,
  AssistantStepResult
} from '../api/assistant.api';
import styles from './assistant.module.css';
import { ConflictResolver } from './ConflictResolver';

/**
 * Назначение: карточка плана действий помощника — проверки, правка параметров, подтверждение и отчёт.
 * Кто: администратор организации (assistant:use).
 * Вход: ответ чата с планом или активный план из истории диалога.
 */

const PARAM_LABELS: Record<string, string> = {
  slot_date: 'Дата',
  start_time: 'Начало',
  end_time: 'Окончание',
  branch_id: 'Филиал',
  service_id: 'Услуга',
  trainer_user_id: 'Тренер',
  client_id: 'Клиент',
  booking_id: 'Запись',
  slot_id: 'Занятие',
  space_id: 'Зал',
  subscription_plan_id: 'Абонемент',
  represented_minor_id: 'Ребёнок',
  max_participants: 'Мест',
  weekday: 'День недели',
  level: 'Что меняем',
  day_off: 'Выходной',
  full_name: 'Имя',
  phone: 'Телефон',
  email: 'Email',
  comment: 'Комментарий',
  status: 'Статус',
  days: 'Дней',
  task_id: 'Задача',
  title: 'Название',
  description: 'Описание',
  column_id: 'Колонка',
  assignee_user_id: 'Ответственный'
};

export const ACTION_LABELS: Record<string, string> = {
  'schedule.slot.create': 'Создать занятие',
  'schedule.slot.move': 'Перенести занятие',
  'schedule.slot.cancel': 'Отменить занятие',
  'schedule.slot.delete': 'Удалить занятие',
  'schedule.work_hours.update': 'Изменить часы',
  'booking.for_client': 'Записать клиента',
  'booking.move': 'Перенести запись',
  'booking.cancel': 'Отменить запись',
  'booking.delete': 'Удалить запись',
  'booking.comment': 'Добавить комментарий к записи',
  'booking.status.update': 'Изменить статус записи',
  'client.resolve': 'Найти клиента',
  'trainer.resolve': 'Выбрать тренера',
  'client.create': 'Создать клиента',
  'client.update': 'Обновить данные клиента',
  'trainer.create': 'Создать тренера',
  'subscription.assign': 'Назначить абонемент',
  'subscription.freeze': 'Заморозить абонемент',
  'subscription.unfreeze': 'Разморозить абонемент',
  'task.create': 'Создать задачу',
  'task.move': 'Переместить задачу'
};

const RESULT_LABELS: Record<string, string> = {
  slot_id: 'Занятие',
  booking_id: 'Запись',
  client_id: 'Клиент',
  trainer_id: 'Тренер',
  trainer_user_id: 'Тренер',
  subscription_id: 'Абонемент',
  cancelled_bookings: 'Отменено записей',
  task_id: 'Задача'
};

const PII_PARAMS = new Set(['full_name', 'phone', 'email']);

export type ErrorReportPayload = {
  planId?: number;
  stepId?: string;
  action?: string;
  error?: string;
  details?: string;
};

function checkIcon(status: AssistantCheck['status']) {
  switch (status) {
    case 'ok':
      return <CheckCircle2 size={16} className={styles.checkOk} aria-hidden />;
    case 'blocked':
      return <XCircle size={16} className={styles.checkBlocked} aria-hidden />;
    case 'warning':
      return <AlertTriangle size={16} className={styles.checkWarning} aria-hidden />;
    default:
      return <Clock size={16} className={styles.checkPending} aria-hidden />;
  }
}

function statusLabel(status?: string): string {
  switch (status) {
    case 'executed':
      return 'Выполнено';
    case 'failed':
      return 'Ошибка';
    case 'expired':
      return 'Устарел';
    case 'cancelled':
      return 'Отменено';
    case 'executing':
      return 'Выполняется';
    default:
      return 'Черновик';
  }
}

function statusClass(status?: string): string {
  switch (status) {
    case 'executed':
      return styles.statusOk;
    case 'failed':
      return styles.statusError;
    case 'expired':
    case 'cancelled':
      return styles.statusMuted;
    default:
      return styles.statusDraft;
  }
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

/** Параметры с выбором из справочника (селект с названиями вместо ввода ID). */
const SELECT_PARAMS = new Set(['branch_id', 'service_id', 'space_id', 'trainer_user_id', 'subscription_plan_id']);
/** Параметры-ссылки на конкретную запись/занятие/клиента — показываем названием, без ручного ввода ID. */
const READONLY_ID_PARAMS = new Set(['client_id', 'booking_id', 'slot_id', 'represented_minor_id']);

const WEEKDAY_LABELS = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];

type ResultLink = { label: string; to: string };

/** Ссылки на созданное/изменённое по итогам шага: занятие, клиент, тренер. */
function resultLinks(step: AssistantStepResult): ResultLink[] {
  const result = step.result ?? {};
  const links: ResultLink[] = [];
  const num = (v: unknown) => (typeof v === 'number' && v > 0 ? v : null);

  const slotID = num(result.slot_id);
  if (slotID) {
    links.push({ label: 'Открыть занятие', to: `${ROUTE_PATHS.schedule}?open_slot=${slotID}` });
  }
  const clientID = num(result.client_id);
  if (clientID) {
    links.push({ label: 'Карточка клиента', to: `/clients/${clientID}` });
  }
  const trainerID = num(result.trainer_id) ?? num(result.trainer_user_id);
  if (trainerID) {
    links.push({ label: 'Профиль тренера', to: `/trainers/${trainerID}` });
  }
  return links;
}

function resultSummary(step: AssistantStepResult): string {
  const r = step.result;
  if (!r) return '';
  const parts: string[] = [];
  for (const [k, v] of Object.entries(r)) {
    if (v === null || v === undefined || v === '') continue;
    const label = RESULT_LABELS[k] ?? k;
    if (typeof v === 'boolean') {
      if (v) parts.push(label);
    } else {
      parts.push(`${label}: ${String(v)}`);
    }
  }
  return parts.join(' · ');
}

export interface AssistantProposalCardProps {
  proposal: AssistantProposal;
  busy: boolean;
  onExecute: (planId: number) => Promise<void>;
  onCancel: (planId: number) => Promise<void>;
  onRevalidate: (planId: number, plan: AssistantPlan) => Promise<boolean>;
  onReportError?: (report: ErrorReportPayload) => void;
}

export function AssistantProposalCard({
  proposal,
  busy,
  onExecute,
  onCancel,
  onRevalidate,
  onReportError
}: AssistantProposalCardProps) {
  const [editablePlan, setEditablePlan] = useState<AssistantPlan | null>(null);
  const [dirty, setDirty] = useState(false);

  // План для показа и правок: приоритет display_plan (скрыты служебные шаги), иначе plan.
  const basePlan = proposal.display_plan ?? proposal.plan ?? null;

  useEffect(() => {
    if (basePlan) {
      setEditablePlan(JSON.parse(JSON.stringify(basePlan)) as AssistantPlan);
      setDirty(false);
    } else {
      setEditablePlan(null);
      setDirty(false);
    }
  }, [basePlan]);

  const status = proposal.status ?? 'draft';
  const draft = status === 'draft';
  const done = status === 'executed' || status === 'expired' || status === 'cancelled';
  // Бэкенд мог отдать null вместо массива (план без шагов) — карточка не должна падать.
  const resultSteps = proposal.result?.steps ?? [];
  const hasBlockers = (proposal.checks ?? []).some((c) => c.status === 'blocked');
  const canConfirm = !hasBlockers && !done && (draft || status === 'failed');
  const canCancel = !done;
  const canRevalidate = draft && !done;

  const hasFailed =
    status === 'failed' ||
    proposal.result?.status === 'failed' ||
    Boolean(proposal.result?.failed_step) ||
    Boolean(proposal.result?.error);

  const expiresLabel = useMemo(() => {
    if (!proposal.expires_at) return '';
    const d = new Date(proposal.expires_at);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }, [proposal.expires_at]);

  function updateParam(stepIndex: number, key: string, value: unknown) {
    if (!editablePlan) return;
    const next: AssistantPlan = JSON.parse(JSON.stringify(editablePlan)) as AssistantPlan;
    const step = next.steps[stepIndex];
    if (!step) return;
    step.params = { ...step.params, [key]: value };
    setEditablePlan(next);
    setDirty(true);
  }

  async function checkAgain() {
    if (!editablePlan || busy) return;
    const ok = await onRevalidate(proposal.plan_id, editablePlan);
    if (ok) setDirty(false);
  }

  async function confirm() {
    if (dirty) {
      if (!editablePlan) return;
      const ok = await onRevalidate(proposal.plan_id, editablePlan);
      if (!ok) return;
      setDirty(false);
    }
    await onExecute(proposal.plan_id);
  }

  return (
    <div className={styles.proposalCard} data-testid="assistant-proposal-card">
      <div className={styles.proposalHeader}>
        <div>
          <span className={`${styles.statusBadge} ${statusClass(status)}`}>{statusLabel(status)}</span>
          {expiresLabel && draft ? <span className={styles.expiresHint}>Действует до {expiresLabel}</span> : null}
        </div>
        <span className={styles.planIdHint}>План #{proposal.plan_id}</span>
      </div>

      <p className={styles.proposalSummary}>{proposal.summary}</p>

      {proposal.checks && proposal.checks.length > 0 ? (
        <div className={styles.checksBlock}>
          <p className={styles.proposalTitle}>Проверки перед выполнением</p>
          <ul className={styles.checksList}>
            {proposal.checks.map((chk) => (
              <li key={`${chk.code}-${chk.step_id ?? ''}`} className={styles.checkItem}>
                {checkIcon(chk.status)}
                <div className={styles.checkContent}>
                  <span className={styles.checkTitle}>{chk.title}</span>
                  {chk.message ? <span className={styles.checkMessage}>{chk.message}</span> : null}
                  {chk.status === 'blocked' && draft && (
                    <ConflictResolver
                      check={chk}
                      proposal={proposal}
                      stepIndex={0}
                      onUpdateParam={updateParam}
                      onRevalidate={() => void checkAgain()}
                    />
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {editablePlan && (editablePlan.steps?.length ?? 0) > 0 ? (
        <div className={styles.stepsBlock}>
          <p className={styles.proposalTitle}>Параметры</p>
          {editablePlan.steps.map((step, si) => {
            const stepActionName = ACTION_LABELS[step.action] ?? step.action;
            const entries = Object.entries(step.params).filter(([k]) => !PII_PARAMS.has(k));
            if (entries.length === 0) return null;
            return (
              <div key={step.id} className={styles.stepForm}>
                <div className={styles.stepHeader}>
                  <span className={styles.stepAction}>{stepActionName}</span>
                </div>
                {entries.map(([key, value]) => {
                  const label = PARAM_LABELS[key] ?? key;
                  if (typeof value === 'boolean') {
                    return (
                      <label key={key} className={styles.paramCheckbox}>
                        <span>{label}</span>
                        <input
                          type="checkbox"
                          checked={value}
                          disabled={busy || done}
                          onChange={(e) => updateParam(si, key, e.target.checked)}
                        />
                      </label>
                    );
                  }
                  const stepLabel = proposal.labels?.[step.id]?.[key];
                  if (READONLY_ID_PARAMS.has(key)) {
                    return (
                      <div key={key} className={styles.paramField}>
                        <span>{label}</span>
                        <div className={styles.readonlyParam}>
                          {stepLabel ? (
                            <span className={styles.paramNamedValue}>{stepLabel}</span>
                          ) : (
                            <span className={styles.paramIdValue}>#{formatValue(value)}</span>
                          )}
                        </div>
                      </div>
                    );
                  }
                  if (SELECT_PARAMS.has(key)) {
                    const allOpts: AssistantParamOption[] = proposal.options?.[key] ?? [];
                    const curBranch = step.params.branch_id ? Number(step.params.branch_id) : null;
                    const filtered =
                      curBranch && (key === 'service_id' || key === 'space_id' || key === 'trainer_user_id')
                        ? allOpts.filter((o) => !o.branch_id || o.branch_id === curBranch)
                        : allOpts;
                    const curVal = value === null || value === undefined ? '' : String(value);
                    const hasCur = filtered.some((o) => String(o.id) === curVal);
                    return (
                      <label key={key} className={styles.paramField}>
                        <span>{label}</span>
                        <select
                          className={styles.paramInput}
                          value={curVal}
                          disabled={busy || done}
                          onChange={(e) =>
                            updateParam(si, key, e.target.value === '' ? null : Number(e.target.value))
                          }
                        >
                          <option value="">Выберите {label.toLowerCase()}...</option>
                          {!hasCur && curVal && stepLabel ? (
                            <option value={curVal}>{stepLabel}</option>
                          ) : !hasCur && curVal ? (
                            <option value={curVal}>#{curVal}</option>
                          ) : null}
                          {filtered.map((o) => (
                            <option key={o.id} value={o.id}>
                              {o.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    );
                  }
                  if (key === 'weekday' && typeof value === 'number') {
                    return (
                      <label key={key} className={styles.paramField}>
                        <span>{label}</span>
                        <select
                          className={styles.paramInput}
                          value={String(value)}
                          disabled={busy || done}
                          onChange={(e) => updateParam(si, key, Number(e.target.value))}
                        >
                          {WEEKDAY_LABELS.map((d, i) => (
                            <option key={d} value={i}>
                              {d}
                            </option>
                          ))}
                        </select>
                      </label>
                    );
                  }
                  if (key === 'status') {
                    const statuses = [
                      { id: 'pending', name: 'Ожидает' },
                      { id: 'confirmed', name: 'Подтверждено' },
                      { id: 'cancelled', name: 'Отменено' }
                    ];
                    return (
                      <label key={key} className={styles.paramField}>
                        <span>{label}</span>
                        <select
                          className={styles.paramInput}
                          value={formatValue(value)}
                          disabled={busy || done}
                          onChange={(e) => updateParam(si, key, e.target.value)}
                        >
                          <option value="">Выберите статус</option>
                          {statuses.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    );
                  }
                  const numeric = [
                    'branch_id',
                    'service_id',
                    'trainer_user_id',
                    'client_id',
                    'booking_id',
                    'slot_id',
                    'space_id',
                    'subscription_plan_id',
                    'represented_minor_id',
                    'max_participants',
                    'weekday',
                    'days',
                    'task_id',
                    'column_id'
                  ].includes(key);
                  const isTime = key === 'start_time' || key === 'end_time';
                  const isDate = key === 'slot_date' || key === 'date';
                  return (
                    <label key={key} className={styles.paramField}>
                      <span>{label}</span>
                      <input
                        className={styles.paramInput}
                        type={isTime ? 'time' : isDate ? 'date' : numeric ? 'number' : 'text'}
                        value={isTime && typeof value === 'string' ? value.slice(0, 5) : formatValue(value)}
                        disabled={busy || done}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (isTime) {
                            updateParam(si, key, val ? `${val}:00` : '');
                          } else if (numeric) {
                            updateParam(si, key, val ? Number(val) : null);
                          } else {
                            updateParam(si, key, val);
                          }
                        }}
                      />
                    </label>
                  );
                })}
              </div>
            );
          })}
        </div>
      ) : null}

      {dirty ? <p className={styles.dirtyHint}>Параметры изменены — нажмите «Проверить снова».</p> : null}

      {resultSteps.length > 0 ? (
        <div className={styles.resultBlock}>
          <p className={styles.proposalTitle}>Отчёт по шагам</p>
          <ul className={styles.resultList}>
            {resultSteps.map((step) => (
              <li key={step.id} className={styles.resultRow}>
                <span className={styles.resultAction}>{ACTION_LABELS[step.action] ?? step.action}</span>
                <span className={step.status === 'done' ? styles.resultDone : styles.resultFailed}>
                  {step.status === 'done' ? 'готово' : step.error || 'не выполнено'}
                </span>
                {step.status === 'done' && resultSummary(step) ? (
                  <span className={styles.resultDetails}>{resultSummary(step)}</span>
                ) : null}
                {step.status === 'done' && resultLinks(step).length > 0 ? (
                  <span className={styles.resultDetails}>
                    {resultLinks(step).map((l) => (
                      <Link key={l.to} className={styles.proposalLink} to={l.to}>
                        {l.label}
                      </Link>
                    ))}
                  </span>
                ) : null}
                {step.checks && step.checks.length > 0 ? (
                  <span className={styles.resultDetails}>
                    {step.checks
                      .filter((c) => c.status === 'ok')
                      .map((c) => c.title)
                      .join(' · ')}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className={styles.proposalActions}>
        <Button
          onClick={() => void confirm()}
          disabled={!canConfirm || (dirty && busy)}
          data-testid="assistant-proposal-confirm"
        >
          {status === 'failed' ? 'Продолжить' : 'Сделать'}
        </Button>
        {canCancel ? (
          <Button
            variant="secondary"
            onClick={() => void onCancel(proposal.plan_id)}
            disabled={busy}
            data-testid="assistant-proposal-cancel"
          >
            Отмена
          </Button>
        ) : null}
        <Button variant="ghost" onClick={() => void checkAgain()} disabled={busy || !draft || !canRevalidate}>
          Проверить снова
        </Button>
        {hasFailed ? (
          <Button
            variant="secondary"
            onClick={() => {
              const failedStep = proposal.result?.steps?.find((s) => s.status === 'failed' || Boolean(s.error));
              onReportError?.({
                planId: proposal.plan_id,
                stepId: failedStep?.id ?? proposal.result?.failed_step,
                action: failedStep?.action,
                error: failedStep?.error ?? proposal.result?.error ?? 'Ошибка выполнения плана'
              });
            }}
            data-testid="assistant-proposal-report-error"
          >
            Сообщить разработчикам
          </Button>
        ) : null}
        <Link className={styles.proposalLink} to={ROUTE_PATHS.schedule}>
          Открыть расписание
        </Link>
        <Link className={styles.proposalLink} to={ROUTE_PATHS.bookings}>
          Открыть записи
        </Link>
      </div>
    </div>
  );
}
